import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import WebsiteAutocomplete from "./WebsiteAutocomplete";

export default function TopBar({
  title,
  subtitle,
  websites = [],
  activeWebsiteId = "",
  onChangeWebsite,
  showWebsitePicker = false,
  showReportsActions = false,
  action
}) {
  const [showSwitcher, setShowSwitcher] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedWebsite = useMemo(
    () => websites.find((w) => String(w.id) === String(activeWebsiteId)) || null,
    [websites, activeWebsiteId]
  );
  const websiteHref = useMemo(() => {
    const raw = String(selectedWebsite?.url || "").trim();
    if (!raw) return "https://www.charterglobal.com";
    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }
    return `https://${raw}`;
  }, [selectedWebsite]);

  const triggerWorkspaceAction = (eventName, fallbackRoute = "") => {
    const isInReports = location.pathname === "/tools/wpa/dashboard/reports";
    const isInQuestionnaire = location.pathname === "/tools/wpa/dashboard/questionnaire";
    const isInIncidents = location.pathname === "/tools/wpa/dashboard/incidents";
    const shouldDispatchDirectly = (
      (eventName.startsWith("wpa:reports-") && isInReports)
      || (eventName.startsWith("wpa:questionnaire-") && isInQuestionnaire)
      || (eventName.startsWith("wpa:incidents-") && isInIncidents)
    );
    if (shouldDispatchDirectly) {
      window.dispatchEvent(new CustomEvent(eventName));
      return;
    }
    if (fallbackRoute) {
      navigate(fallbackRoute, { state: { pendingWorkspaceAction: eventName } });
      return;
    }
    window.dispatchEvent(new CustomEvent(eventName));
  };

  const renderWorkspaceActions = () => {
    if (location.pathname === "/tools/wpa/dashboard/reports") {
      return (
        <div className="workspace-report-actions">
          <button
            type="button"
            className="workspace-action-btn"
            onClick={() => triggerWorkspaceAction("wpa:reports-generate", "/tools/wpa/dashboard/reports")}
          >
            Generate AI Starter
          </button>
          <button
            type="button"
            className="workspace-action-btn"
            onClick={() => triggerWorkspaceAction("wpa:reports-save", "/tools/wpa/dashboard/reports")}
          >
            Save Draft
          </button>
        </div>
      );
    }

    if (location.pathname === "/tools/wpa/dashboard/questionnaire") {
      return (
        <div className="workspace-report-actions">
          <button
            type="button"
            className="workspace-action-btn"
            onClick={() => triggerWorkspaceAction("wpa:questionnaire-generate-stage", "/tools/wpa/dashboard/questionnaire")}
          >
            Generate AI Stage
          </button>
          <button
            type="button"
            className="workspace-action-btn"
            onClick={() => triggerWorkspaceAction("wpa:questionnaire-save-stage", "/tools/wpa/dashboard/questionnaire")}
          >
            Save Stage
          </button>
        </div>
      );
    }

    if (location.pathname === "/tools/wpa/dashboard/incidents") {
      return (
        <div className="workspace-report-actions">
          <button
            type="button"
            className="workspace-action-btn"
            onClick={() => triggerWorkspaceAction("wpa:incidents-generate-form", "/tools/wpa/dashboard/incidents")}
          >
            Generate AI Incident
          </button>
        </div>
      );
    }

    return null;
  };

  if (showWebsitePicker) {
    return (
      <div className="workspace-top-shell-outer">
        <header className="top-shell page-head workspace-top-shell">
          <div className="workspace-context">
            <p className="eyebrow">YOU ARE IN WORDPRESS ASSESSMENT TOOL</p>
            <div className="workspace-title-row">
              <h2>
                <a className="workspace-title-link" href={websiteHref} target="_blank" rel="noreferrer">
                  {String(selectedWebsite?.name || "No Website Selected").toUpperCase()}
                </a>
              </h2>
              <button
                type="button"
                className="workspace-change-btn"
                onClick={() => setShowSwitcher((prev) => !prev)}
              >
                {showSwitcher ? "Close" : "Change"}
              </button>
            </div>
          </div>
          <div className="top-actions workspace-actions">
            {showReportsActions ? (
              renderWorkspaceActions()
            ) : null}
            {showSwitcher ? (
              <WebsiteAutocomplete
                websites={websites}
                activeWebsiteId={activeWebsiteId}
                onSelectWebsite={(id) => {
                  onChangeWebsite?.(id);
                  setShowSwitcher(false);
                }}
                placeholder="Search website workspace..."
              />
            ) : null}
            {action || null}
          </div>
        </header>
      </div>
    );
  }

  return (
    <header className="top-shell page-head">
      <div>
        <p className="eyebrow">Page Context</p>
        <h2>{title}</h2>
        {subtitle ? <p className="muted">{subtitle}</p> : null}
      </div>
      <div className="top-actions">{action || null}</div>
    </header>
  );
}
